import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MapPin, Calendar, Users, Target, CheckCircle2, Circle } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";
import { CATEGORIES, type Category } from "@/lib/categories";

export const Route = createFileRoute("/projects/$id")({
  component: ProjectDetail,
});

function ProjectDetail() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [updates, setUpdates] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [newUpdate, setNewUpdate] = useState("");

  const load = async () => {
    setLoading(true);
    const { data: p } = await supabase.from("projects").select("*, creator:profiles!projects_created_by_fkey(username, full_name, avatar_url)").eq("id", id).maybeSingle();
    setProject(p);
    const [{ data: ms }, { data: ups }, { data: mems }] = await Promise.all([
      supabase.from("project_milestones").select("*").eq("project_id", id).order("position"),
      supabase.from("project_updates").select("*, author:profiles!project_updates_author_id_fkey(username, full_name, avatar_url)").eq("project_id", id).order("created_at", { ascending: false }),
      supabase.from("project_members").select("user_id, role, profiles:profiles!project_members_user_id_fkey(username, full_name, avatar_url)").eq("project_id", id),
    ]);
    setMilestones(ms ?? []);
    setUpdates(ups ?? []);
    setMembers(mems ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const isMember = !!members.find((m) => m.user_id === user?.id);

  const join = async () => {
    if (!user) { toast.error("Sign in to join"); return; }
    setJoining(true);
    if (isMember) {
      await supabase.from("project_members").delete().eq("project_id", id).eq("user_id", user.id);
    } else {
      await supabase.from("project_members").insert({ project_id: id, user_id: user.id });
    }
    await load();
    setJoining(false);
  };

  const postUpdate = async () => {
    if (!user || !newUpdate.trim()) return;
    const { error } = await supabase.from("project_updates").insert({ project_id: id, author_id: user.id, content: newUpdate.trim() });
    if (error) { toast.error(error.message); return; }
    setNewUpdate("");
    await load();
  };

  if (loading) return <AppShell><div className="py-20 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div></AppShell>;
  if (!project) return <AppShell><p className="py-20 text-center text-muted-foreground">Project not found.</p></AppShell>;

  const cat = CATEGORIES[project.category as Category];
  const completedMs = milestones.filter((m) => m.completed).length;
  const progress = milestones.length ? Math.round((completedMs / milestones.length) * 100) : 0;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        {project.cover_url && (
          <div className="mb-6 overflow-hidden rounded-2xl border border-border">
            <img src={project.cover_url} alt="" className="h-64 w-full object-cover" />
          </div>
        )}

        <div className="grid gap-8 md:grid-cols-[1fr_280px]">
          <div className="min-w-0">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1"><cat.icon className="h-3 w-3" style={{ color: cat.color }} />{cat.label}</Badge>
              <Badge variant="outline" className="capitalize">{project.status.replace("_", " ")}</Badge>
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">{project.title}</h1>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{project.description}</p>

            {milestones.length > 0 && (
              <section className="mt-8">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-display text-lg font-semibold">Milestones</h2>
                  <span className="text-sm text-muted-foreground">{completedMs}/{milestones.length} done · {progress}%</span>
                </div>
                <div className="mb-3 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-success transition-all" style={{ width: `${progress}%` }} />
                </div>
                <ul className="divide-y divide-border rounded-xl border border-border bg-card">
                  {milestones.map((m) => (
                    <li key={m.id} className="flex items-start gap-3 p-4">
                      {m.completed ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" /> : <Circle className="mt-0.5 h-5 w-5 text-muted-foreground" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{m.title}</p>
                        {m.description && <p className="mt-0.5 text-sm text-muted-foreground">{m.description}</p>}
                        {m.due_date && <p className="mt-1 text-xs text-muted-foreground">Due {format(new Date(m.due_date), "MMM d, yyyy")}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-8">
              <h2 className="mb-3 font-display text-lg font-semibold">Updates</h2>
              {isMember && (
                <div className="mb-4 rounded-xl border border-border bg-card p-4">
                  <Textarea value={newUpdate} onChange={(e) => setNewUpdate(e.target.value)} placeholder="Share progress, photos or news…" rows={3} />
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" onClick={postUpdate} disabled={!newUpdate.trim()}>Post update</Button>
                  </div>
                </div>
              )}
              {updates.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">No updates yet.</p>
              ) : (
                <div className="space-y-3">
                  {updates.map((u) => (
                    <article key={u.id} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Avatar className="h-6 w-6"><AvatarImage src={u.author?.avatar_url} /><AvatarFallback>{u.author?.username?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                        <span className="font-medium text-foreground">{u.author?.username}</span>
                        <span>· {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}</span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm">{u.content}</p>
                      {u.image_url && <img src={u.image_url} alt="" className="mt-3 max-h-80 w-full rounded-lg object-cover" />}
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <Button onClick={join} disabled={joining} className="w-full" variant={isMember ? "outline" : "default"}>
                {isMember ? "Leave project" : "Join as volunteer"}
              </Button>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Users className="mt-0.5 h-4 w-4" />
                  <span>{project.volunteer_count ?? members.length}{project.volunteer_target ? ` / ${project.volunteer_target}` : ""} volunteers</span>
                </div>
                {project.location_name && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="mt-0.5 h-4 w-4" /><span>{project.location_name}</span>
                  </div>
                )}
                {project.starts_at && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <Calendar className="mt-0.5 h-4 w-4" /><span>{format(new Date(project.starts_at), "MMM d, yyyy")}</span>
                  </div>
                )}
                {project.impact_metric && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <Target className="mt-0.5 h-4 w-4" /><span>{project.impact_value ?? 0} {project.impact_metric}</span>
                  </div>
                )}
              </dl>
            </div>

            {project.creator && (
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Organizer</p>
                <Link to="/profile/$username" params={{ username: project.creator.username }} className="flex items-center gap-3 hover:bg-accent -m-2 p-2 rounded-md">
                  <Avatar className="h-9 w-9"><AvatarImage src={project.creator.avatar_url} /><AvatarFallback>{project.creator.username?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                  <div>
                    <p className="text-sm font-medium">{project.creator.full_name || project.creator.username}</p>
                    <p className="text-xs text-muted-foreground">@{project.creator.username}</p>
                  </div>
                </Link>
              </div>
            )}

            {members.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Volunteers</p>
                <div className="flex flex-wrap gap-2">
                  {members.slice(0, 12).map((m) => (
                    <Link key={m.user_id} to="/profile/$username" params={{ username: m.profiles?.username ?? "" }}>
                      <Avatar className="h-8 w-8 ring-2 ring-background"><AvatarImage src={m.profiles?.avatar_url} /><AvatarFallback className="text-[10px]">{m.profiles?.username?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
